import { useContext } from 'react';

import { faCircleInfo } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/videa-components';

import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ExternalPatientContext } from '../constants';

const PatientEducationContent = () => {
  return (
    <>
      <p>
        <Trans>
          The portion of total patients who may have benefited from AI as an
          educational tool during their visits.
        </Trans>
      </p>
      <p>
        <Trans>
          <b>Patients seen:</b> Total unique patients whose images were analyzed
          by AI.
        </Trans>
      </p>
      <p>
        <Trans>
          <b>Patients needing treatment:</b> Patients who are likely to need at
          least one treatment according to AI.
        </Trans>
      </p>
      <p>
        <Trans>
          <b>Patients educated with AI:</b> Patients who may have been shown AI
          as part of their diagnosis and treatment discussion (AI-annotated
          images and/or patient education materials were viewed).
        </Trans>
      </p>
    </>
  );
};

const CaseAcceptanceContent = () => {
  return (
    <>
      <p>
        <Trans>
          The portion of treatment plans scheduled and completed after AI
          results suggested a relevant treatment was likely needed.
        </Trans>
      </p>
      <p>
        <Trans>
          <b>Treatments planned with AI:</b> Treatment plans that include at
          least one AI-recommended treatment and are marked as planned in your
          PMS.
        </Trans>
      </p>
      <p>
        <Trans>
          <b>Treatments scheduled:</b> The subset of planned treatment plans
          above that progressed to scheduled status, indicating patient
          acceptance.
        </Trans>
      </p>
      <p>
        <Trans>
          <b>Treatments completed:</b> The subset of scheduled treatments above
          that were completed.
        </Trans>
      </p>
    </>
  );
};

const TreatmentPlanningContent = () => {
  return (
    <>
      <p>
        <Trans>
          Compares the average treatment plan value per treated patient before
          and after adopting AI, either across all AI-relevant treatment
          categories or for a specific treatment type.
        </Trans>
      </p>
      <p>
        <Trans>
          <b>Post-AI period:</b> From when your practice started using AI up to
          today.
        </Trans>
      </p>
      <p>
        <Trans>
          <b>Pre-AI period:</b> The same number of days before AI adoption as
          the period since adoption.
        </Trans>
      </p>
    </>
  );
};

interface Props {
  type: 'patient-education' | 'case-acceptance' | 'treatment-planning';
}

export const AIImpactTooltip = ({ type }: Props) => {
  const externalPatientContext = useContext(ExternalPatientContext);
  const { trackEvent } = useTrackEvent();

  const getTooltipContent = () => {
    switch (type) {
      case 'patient-education':
        return <PatientEducationContent />;
      case 'case-acceptance':
        return <CaseAcceptanceContent />;
      case 'treatment-planning':
        return <TreatmentPlanningContent />;
    }
  };

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip
        onOpenChange={(open) => {
          if (open) {
            trackEvent(mixpanelEvents.externalAiImpactTooltipOpen, {
              ...externalPatientContext,
              metric: type,
            });
          }
        }}
      >
        <TooltipTrigger asChild>
          <FontAwesomeIcon
            icon={faCircleInfo}
            className="vai-h-3 vai-w-3 vai-text-zinc-100"
          />
        </TooltipTrigger>
        <TooltipContent className="vai-border-zinc-200 vai-w-60">
          <div className="vai-flex vai-flex-col vai-gap-2 vai-font-inter vai-text-sm">
            {getTooltipContent()}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
